import {
  EntityStatusInput,
  PageWrapper,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import HistoryLogTable from "@/components/History/HistoryLogTable";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import {
  CONTENT_WIDTH,
  FieldType,
  GUTTER_SIZE,
  ValueType,
} from "@/enums";
import {
  buildProjectStatusOptions,
  routes,
  selectFilterHelper,
} from "@/helpers";
import { handleServerErrorResponse } from "@/helpers/handleServerErrorResponse";
import { useFlatFormAvailability } from "@/hooks";
import {
  useGetAdminFlatFormsQuery,
  useGetAdminFormsQuery,
  useGetFormPropertyQuery,
  useGetProductCategoryFormsQuery,
  useUpdateFormPropertyMutation,
} from "@/services";
import { Card, Checkbox, Col, Form, Row, Select, Typography } from "antd";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ConfirmDuplicateFormPropertyValuesModal } from "../ConfirmDuplicateFormPropertyValuesModal";
import { AttachmentEditFormField } from "./components/AttachmentEditFormField";
import { AutoCompleteEditFormField } from "./components/AutoCompleteEditFormField";
import { DateEditFormField } from "./components/DateEditFormField";
import { DateRangeFormField } from "./components/DateRangeFormField";
import { GenericEditFormField } from "./components/GenericEditFormField";
import { NumberEditFormField } from "./components/NumberEditFormField";
import { OptionsEditFormField } from "./components/OptionsEditFormField";
import { PercentCompleteSliderEditFormField } from "./components/PercentCompleteSliderEditFormField";
import { SwitchEditField } from "./components/SwitchEditField";

const { Title, Paragraph } = Typography;

export const EditFormField = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { formType, id } = useParams();

  const formRef = useRef(null);
  const [availableValues, setAvailableValues] = useState([]);
  const [parentId, setParentId] = useState();
  const [duplicateMessages, setDuplicateMessages] = useState(null);
  const [fieldTypeForm, setFieldTypeForm] = useState();

  const { availableParent, availableDateFields } = useFlatFormAvailability(id);

  const [availableStatuses, setAvailableStatuses] = useState([]);

  const {
    data: formProperty,
    isFetching: formPropertyFetching,
    refetch: refetchFormProperty,
  } = useGetFormPropertyQuery({ formType, id }, { skip: !formType && !id });

  const { refetch: refetchAdminForms } = useGetAdminFormsQuery();
  const { refetch: refetchForms } = useGetProductCategoryFormsQuery();

  const {
    data: { flatForms, categories } = {},
    isFetching: flatFormsFetching,
  } = useGetAdminFlatFormsQuery();

  const isFetching = formPropertyFetching || flatFormsFetching;

  const [updateFormProperty, { isLoading: isSaving }] =
    useUpdateFormPropertyMutation();

  useEffect(() => {
    if (parentId) return;

    setAvailableValues([]);
    const curProp = formRef.current?.getFieldValue("property");
    if (curProp?.dependsOnPropertyValues) {
      curProp.dependsOnPropertyValues = [];
      formRef.current.setFieldsValue({
        property: curProp,
      });
    }
  }, [parentId]);

  useEffect(() => {
    if (!parentId || !flatForms) return;

    const index = flatForms?.findIndex(
      (field) => field.property.id === parentId
    );

    const { values: formValues, property } = flatForms[index];

    if (!formValues) return;

    const values = formValues.map((value) => ({
      value: value.id,
      text: intl.formatMessage({
        id: value.label.id,
        defaultMessage: value.label.fallback,
      }),
    }));

    if (property?.fieldType === FieldType.Switch) {
      values.push({ value: "true", text: "True" });
      values.push({ value: "false", text: "False" });
    }

    setAvailableValues(values);

    if (!formRef.current) return;

    const ids = values.map(({ value: id }) => id);
    const curProp = formRef.current.getFieldValue("property");

    if (!curProp.dependsOnPropertyValues) return;

    curProp.dependsOnPropertyValues = curProp.dependsOnPropertyValues.filter(
      (field) => ids.includes(field)
    );
    formRef.current.setFieldsValue({
      property: curProp,
    });
  }, [flatForms, intl, parentId]);

  useEffect(() => {
    if (formProperty?.property?.id) {
      setParentId(formProperty.property.dependsOnPropertyId);
    }
  }, [formProperty?.property?.dependsOnPropertyId, formProperty?.property?.id]);

  const onParentIdChange = (value) => {
    setParentId(value);
    formRef.current.setFieldsValue({ dependsOnPropertyId: value });
  };

  const handleSave = (form, confirmedSave) => {
    let dupeOptions = null;

    if (!confirmedSave) {
      dupeOptions = checkDuplicateOptionsConfirmation();
      setDuplicateMessages(dupeOptions);
    }

    if (dupeOptions && !confirmedSave) return;

    const data = {
      ...formProperty,
      ...form,
      property: {
        ...formProperty?.property,
        ...form.property,
      },
    };
    const {
      property: { Placeholder: placeholder },
      requiredValidationText,
    } = data;
    if (placeholder?.fallback === null) {
      delete data.property.placeholder;
    } else if (placeholder) {
      data.property.placeholder.id = formProperty?.property.placeholder?.id;
    }
    if (requiredValidationText?.fallback === null) {
      delete data.property.requiredValidationText;
    } else if (requiredValidationText) {
      data.property.requiredValidationText.id =
        formProperty?.property.requiredValidationText?.id;
    }
    updateFormProperty({
      form: formType,
      property: data.property,
      values: data.values,
    })
      .unwrap()
      .then(() => {
        refetchForms();
        refetchAdminForms();
        refetchFormProperty();
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
        navigate(routes.admin.forms.get(formType));
      })
      .catch((response) => {
        handleServerErrorResponse(response, intl);
      });
    setDuplicateMessages(null);
  };

  useEffect(() => {
    if (!isFetching) {
      const availableStatuses = buildProjectStatusOptions().map((option) => ({
        ...option,
        fallback: intl.formatMessage({ id: option.translationId }),
      }));

      setAvailableStatuses(availableStatuses);
    }
  }, [isFetching, intl]);

  useEffect(() => {
    setFieldTypeForm(() => {
      switch (formProperty?.property?.fieldType) {
        case FieldType.Attachment:
        case FieldType.MultiAttachment:
          return <AttachmentEditFormField />;
        case FieldType.DropDown:
        case FieldType.Checkbox:
        case FieldType.MultiSelectDropDown:
          return (
            <OptionsEditFormField
              availableDependantValues={availableValues}
              form={formRef}
              formProperty={formProperty}
            />
          );
        case FieldType.AutoComplete:
        case FieldType.MultiSelectAutoComplete:
          return <AutoCompleteEditFormField />;
        case FieldType.Number:
          return <NumberEditFormField />;
        case FieldType.PercentCompleteSlider:
          return <PercentCompleteSliderEditFormField />;
        case FieldType.Date:
          return (
            <DateEditFormField availableDateFields={availableDateFields} />
          );
        case FieldType.DateRange:
          return <DateRangeFormField availableParent={availableParent} />;
        case FieldType.Switch:
          return <SwitchEditField />;
        case FieldType.Text:
        case FieldType.TextArea:
        default:
          return <GenericEditFormField />;
      }
    });
  }, [availableDateFields, availableParent, availableValues, formProperty]);

  const checkMappedListForDuplicateKeys = (mappedList) => {
    // checks duplicate keys in object to check option duplicates for dropdown
    const counts = {};
    mappedList.forEach((list) => {
      counts[list] = (counts[list] || 0) + 1;
    });

    return Object.keys(counts).filter((count) => counts[count] > 1);
  };

  const checkDuplicateOptionsConfirmation = () => {
    // Determines if modal should interrupt the save for verification of duplicates
    // will return either mapping of duplicate error messages OR null
    // check users, options, vendors, and location values for duplicates

    const dupeErrors = {};

    const formValues = formRef.current.getFieldsValue().values;

    if (formValues) {
      const userValues = formValues.filter(
        ({ valueType, status }) =>
          valueType === ValueType.FormPropertyUserValue &&
          (status === "active" || status === "preview")
      );

      if (userValues?.length) {
        const userDupes = userValues.map((x) => x.userId);

        const dupes = checkMappedListForDuplicateKeys(userDupes);

        if (dupes?.length) {
          const dupeMessages = [];

          dupes.forEach((userId) => {
            const user = userValues.find((value) => value.userId === userId);
            const name = user?.label?.fallback || user?.label?.fallback;
            dupeMessages.push(name);
          });
          dupeErrors[ValueType.FormPropertyUserValue] = dupeMessages;
        }
      }

      const optionValues = formValues.filter(
        ({ valueType, status }) =>
          valueType === ValueType.FormPropertyValue &&
          (status === "active" || status === "preview")
      );

      if (optionValues?.length) {
        const optionDupes = optionValues.map(
          (option) => option?.label?.fallback
        );

        const dupes = checkMappedListForDuplicateKeys(optionDupes);

        if (dupes?.length) {
          const dupeMessages = [];

          dupes.forEach((optionName) => {
            dupeMessages.push(optionName);
          });
          dupeErrors[ValueType.FormPropertyValue] = dupeMessages;
        }
      }

      const locationValues = formValues.filter(
        ({ valueType, status }) =>
          valueType === ValueType.FormPropertyLocationValue &&
          (status === "active" || status === "preview")
      );

      if (locationValues?.length) {
        const locationDupes = locationValues.map(
          (value) => value?.label?.fallback
        );

        const dupes = checkMappedListForDuplicateKeys(locationDupes);

        if (dupes?.length) {
          const dupeMessages = [];

          dupes.forEach((locationName) => {
            dupeMessages.push(locationName);
          });
          dupeErrors[ValueType.FormPropertyLocationValue] = dupeMessages;
        }
      }
    }

    if (Object.entries(dupeErrors).length) {
      return dupeErrors;
    }

    return null;
  };

  if (isFetching || !formProperty?.property?.id) return <SpinLoader />;

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "editFormField.pageTitle" })}
      backButtonRoute={routes.admin.forms.get(formType)}
      pageActions={
        <StickyActionsHeader
          rightChildren={
            <SaveAndCancel
              pageName="edit-form-field"
              saveOnClick={() => formRef.current.submit()}
              loading={isSaving}
              cancelOnClick={() => navigate(routes.admin.forms.get(formType))}
            />
          }
        />
      }
    >
      <Row gutter={GUTTER_SIZE.large}>
        <Col sm={CONTENT_WIDTH.max} xl={CONTENT_WIDTH.half}>
          <Card>
            <Typography>
              <Title level={2}>
                {intl.formatMessage(
                  { id: "editFormField.contentTitle" },
                  {
                    formType: intl.formatMessage({
                      id: `enum.formType.${formType}`,
                    }),
                  }
                )}
              </Title>
              <Paragraph>
                {intl.formatMessage({ id: "editFormField.contentSubTitle" })}
              </Paragraph>
            </Typography>
            <Form
              ref={formRef}
              name="basic"
              layout="vertical"
              onFinish={handleSave}
              initialValues={formProperty}
            >
              <Form.Item label={<FormattedMessage id="newFormField.name" />}>
                <span className="ant-form-text font-bold">
                  {formProperty?.property.name}
                </span>
              </Form.Item>
              <Form.Item
                label={<FormattedMessage id="newFormField.formTypeTitle" />}
              >
                <span className="ant-form-text font-bold">
                  <FormattedMessage
                    id={`enum.formType.${formType.toLowerCase()}`}
                  />
                </span>
              </Form.Item>
              <Form.Item
                name={["property", "categoryId"]}
                label={<FormattedMessage id="newFormField.category" />}
              >
                <Select
                  className="viper-form-element-max-width"
                  allowClear
                  placeholder={intl.formatMessage({
                    id: "newFormField.categoryPlaceHolder",
                  })}
                >
                  {categories?.map(({ id, label }) => (
                    <Select.Option key={id} value={id}>
                      {intl.formatMessage({
                        id: label?.id || "unknown",
                        defaultMessage: label?.fallback || "unknown",
                      })}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name={["property", "dependsOnPropertyId"]}
                label={<FormattedMessage id="newFormField.parentFieldTitle" />}
              >
                <Select
                  data-test-id="edit-form-field-select-parent"
                  className="viper-form-element-max-width"
                  onChange={onParentIdChange}
                  allowClear
                >
                  {availableParent?.key && availableParent?.items.length && (
                    <Select.OptGroup
                      label={availableParent.key}
                      key={availableParent.key}
                    >
                      {availableParent.items.map(({ value, text }) => (
                        <Select.Option key={value} value={value}>
                          {text}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  )}
                </Select>
              </Form.Item>
              <Form.Item
                name={["property", "dependsOnPropertyValues"]}
                label={
                  <FormattedMessage id="newFormField.dependentValuesTitle" />
                }
              >
                <Select
                  mode="multiple"
                  className="viper-form-element-max-width"
                  disabled={!availableValues?.length}
                  allowClear
                  placeholder={intl.formatMessage({
                    id: "newFormField.dependentValuesPlaceholder",
                  })}
                  filterOption={selectFilterHelper}
                >
                  {availableValues.map(({ value, text }) => (
                    <Select.Option value={value} key={value}>
                      {text}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="FieldType"
                label={<FormattedMessage id="newFormField.fieldTypeTitle" />}
              >
                <span className="ant-form-text font-bold">
                  <FormattedMessage
                    id={`enum.fieldType.${formProperty?.property.fieldType.toLowerCase()}`}
                  />
                </span>
              </Form.Item>
              <Form.Item
                name={["property", "trackHistory"]}
                valuePropName="checked"
              >
                <Checkbox>
                  {intl.formatMessage({
                    id: "newFormField.fieldHistoryLabel",
                  })}
                </Checkbox>
              </Form.Item>
              {fieldTypeForm}
              <Form.Item
                name={["property", "requiredForStatuses"]}
                label={
                  <FormattedMessage id="newFormField.requiredForStatuses" />
                }
              >
                <Select
                  mode="multiple"
                  allowClear
                  className="viper-form-element-max-width"
                  placeholder={intl.formatMessage({
                    id: "newFormField.requiredForStatusesPlaceholder",
                  })}
                  filterOption={selectFilterHelper}
                >
                  {availableStatuses?.map(({ id, fallback }) => (
                    <Select.Option value={id} key={id}>
                      {fallback}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name={["property", "lockOnSubmission"]}
                valuePropName="checked"
              >
                <Checkbox>
                  {intl.formatMessage({
                    id: "newFormField.lockAfterSubmitTitle",
                  })}
                </Checkbox>
              </Form.Item>
              <EntityStatusInput parentComponent="edit-form-field" />
            </Form>
            <ConfirmDuplicateFormPropertyValuesModal
              isOpen={duplicateMessages != null}
              dupeMessages={duplicateMessages}
              confirmSave={() =>
                handleSave(formRef.current.getFieldsValue(), true)
              }
              cancelSave={() => setDuplicateMessages(null)}
            />
          </Card>
        </Col>
        <Col sm={CONTENT_WIDTH.max} xl={CONTENT_WIDTH.half}>
          <Card loading={formPropertyFetching}>
            <HistoryLogTable
              parentEntityId={id}
              pageSize={15}
              additionalLabels={_.mapValues(
                _.keyBy(formProperty.values, "id"),
                (x) => x.label?.fallback
              )}
            />
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  );
};